/**
 * Orders — what we sold through our own checkout.
 *
 * The `orders` table is overwhelmingly SimpleCirc's: hundreds of thousands
 * of CSV and historical-backfill rows against a handful bought on next.*.
 * This page is only the latter, which is why the endpoint behind it
 * constrains `source` to stripe|paypal|authnet|offline rather than taking
 * free text.
 *
 * Volume here is small and will stay small for a while, so the page is a
 * plain reverse-chronological list rather than a dashboard. The one thing
 * it must not do is read as broken when it is merely early — hence the
 * spelled-out empty state.
 */
import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type SiteOrder, type SiteOrderList } from '../api/client'
import { PubBadge } from '../components/PubBadge'
import { fmtDateTime, fmtInt, fmtMoney, providerLabel } from '../lib/format'

const PAGE_SIZE = 50

const SOURCES = [
  { value: '', label: 'All gateways' },
  { value: 'authnet', label: 'Authorize.Net' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'offline', label: 'Check' },
]

const PUBLICATIONS = [
  { value: '', label: 'All titles' },
  { value: 'flight_journal', label: 'Flight Journal' },
  { value: 'model_airplane_news', label: 'Model Airplane News' },
  { value: 'rc_car_action', label: 'RC Car Action' },
  { value: 'diecast', label: 'Die Cast X' },
]

const TYPES = [
  { value: '', label: 'All types' },
  { value: 'new', label: 'New' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'refund', label: 'Refund' },
]

/** Today's date in Eastern time as YYYY-MM-DD — the server reads the
 *  date filter as Eastern calendar days, so the quick ranges must too. */
function todayET(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date())
}

/** YYYY-MM-DD arithmetic on calendar dates, done in UTC so the local
 *  zone's DST shifts can't move the day. */
function shiftDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

const QUICK_RANGES: { label: string; range: () => [string, string] }[] = [
  {
    label: 'This month',
    range: () => {
      const t = todayET()
      return [`${t.slice(0, 7)}-01`, t]
    },
  },
  {
    label: 'Last month',
    range: () => {
      const end = shiftDays(`${todayET().slice(0, 7)}-01`, -1)
      return [`${end.slice(0, 7)}-01`, end]
    },
  },
  {
    label: 'Last 30 days',
    range: () => {
      const t = todayET()
      return [shiftDays(t, -29), t]
    },
  },
]

export function SiteOrdersPage() {
  const [page, setPage] = useState(0)
  const [source, setSource] = useState('')
  const [publication, setPublication] = useState('')
  const [q, setQ] = useState('')
  const [nr, setNr] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  const params = new URLSearchParams({
    limit: String(PAGE_SIZE),
    offset: String(page * PAGE_SIZE),
  })
  if (source) params.set('source', source)
  if (publication) params.set('publication', publication)
  if (q.trim()) params.set('q', q.trim())
  if (nr) params.set('type', nr)
  if (dateFrom) params.set('date_from', dateFrom)
  if (dateTo) params.set('date_to', dateTo)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['site-orders', source, publication, q.trim(), nr, dateFrom, dateTo, page],
    queryFn: () => api.get<SiteOrderList>(`/api/orders/site?${params.toString()}`),
  })

  const setFilter = (set: (v: string) => void) => (v: string) => {
    set(v)
    setPage(0)
  }

  const items = data?.items ?? []
  const filtered = Boolean(source || publication || q.trim() || nr || dateFrom || dateTo)

  return (
    <div className="p-8 space-y-6 max-w-[1500px]">
      <header className="border-b border-ink-500 pb-6">
        <div className="label mb-1">Members · direct sales</div>
        <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
          Orders
          <span className="text-amber-glow"> ∙ </span>
          <span className="text-paper-trace">
            {fmtInt(data?.order_count)} · {fmtMoney(data?.gross_collected, false, 2)}
          </span>
        </h1>
        <p className="mt-2 text-sm text-paper-dim max-w-[62ch]">
          Every order bought through checkout on the member sites. Orders that
          reached us from SimpleCirc are not listed here — those live on the
          subscriber record and in the daily tie-out on the dashboard.
        </p>
        {data && data.refund_count > 0 && (
          <p className="mt-2 text-sm text-paper-trace">
            {fmtInt(data.refund_count)} refund
            {data.refund_count === 1 ? '' : 's'} totalling{' '}
            {fmtMoney(data.refund_amount, false, 2)}, listed below but not
            counted in the takings.
          </p>
        )}
      </header>

      <div className="flex flex-wrap gap-4">
        <div>
          <div className="label mb-1">Search (email / name / account)</div>
          <div className="relative">
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => {
                setQ(e.target.value)
                setPage(0)
              }}
              placeholder="ada@example.com  ·  Lovelace  ·  57469168"
              className={`bg-ink-900 border border-ink-500 px-3 py-2 text-paper text-[13px] min-w-[320px] ${q ? 'pr-8' : ''}`}
            />
            {q && (
              <button
                type="button"
                aria-label="Clear"
                title="Clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-1 text-paper-dim hover:text-paper text-lg leading-none"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setQ('')
                  setPage(0)
                  searchRef.current?.focus()
                }}
              >×</button>
            )}
          </div>
        </div>
        <div>
          <div className="label mb-1">Paid with</div>
          <select
            value={source}
            onChange={(e) => setFilter(setSource)(e.target.value)}
            className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px] min-w-[180px]"
          >
            {SOURCES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="label mb-1">Title</div>
          <select
            value={publication}
            onChange={(e) => setFilter(setPublication)(e.target.value)}
            className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px] min-w-[220px]"
          >
            {PUBLICATIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="label mb-1">Type</div>
          <select
            value={nr}
            onChange={(e) => setFilter(setNr)(e.target.value)}
            className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px] min-w-[150px]"
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="label mb-1">Ordered (Eastern)</div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              aria-label="From"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setFilter(setDateFrom)(e.target.value)}
              className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px]"
            />
            <span className="text-paper-trace text-[12px]">to</span>
            <input
              type="date"
              aria-label="To"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setFilter(setDateTo)(e.target.value)}
              className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px]"
            />
            {QUICK_RANGES.map((r) => {
              const [from, to] = r.range()
              const active = dateFrom === from && dateTo === to
              return (
                <button
                  key={r.label}
                  type="button"
                  onClick={() => {
                    setDateFrom(from)
                    setDateTo(to)
                    setPage(0)
                  }}
                  className={`px-3 py-2 border font-mono text-[12px] ${
                    active
                      ? 'border-amber-glow text-amber-glow'
                      : 'border-ink-500 text-paper-dim hover:border-amber-glow hover:text-paper'
                  }`}
                >
                  {r.label}
                </button>
              )
            })}
            {(dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => {
                  setDateFrom('')
                  setDateTo('')
                  setPage(0)
                }}
                className="px-2 py-2 font-mono text-[12px] text-paper-dim hover:text-paper"
              >
                Any date ×
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && <p className="text-paper-trace">Loading…</p>}
      {isError && <p className="text-red-400">Couldn't load orders. Try again.</p>}

      {!isLoading && !isError && items.length === 0 && (
        <p className="text-paper-trace max-w-[62ch]">
          {filtered
            ? 'No orders match these filters.'
            : 'No orders bought on the member sites yet. This fills in as the pilot sells — it is not an error.'}
        </p>
      )}

      {items.length > 0 && (
        <div className="border border-ink-500 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-500">
                <Th>When</Th>
                <Th>Buyer</Th>
                <Th>Title</Th>
                <Th>Type</Th>
                <Th>Term</Th>
                <Th>Paid with</Th>
                <Th>Promo</Th>
                <Th>Amount</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <OrderRow key={`${o.source}-${o.order_id}`} order={o} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-[12px] font-mono">
          <span className="text-paper-trace">
            Showing {page * PAGE_SIZE + 1}–
            {Math.min((page + 1) * PAGE_SIZE, data.total)} of {data.total}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1 border border-ink-500 disabled:opacity-30 hover:border-amber-glow"
            >
              ← prev
            </button>
            <button
              disabled={(page + 1) * PAGE_SIZE >= data.total}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1 border border-ink-500 disabled:opacity-30 hover:border-amber-glow"
            >
              next →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function OrderRow({ order: o }: { order: SiteOrder }) {
  const name = o.payer_full_name || o.payer_email || '—'
  return (
    <tr className="border-b border-ink-700/40 hover:bg-ink-800/40">
      <Td mono subtle>{fmtDateTime(o.order_date)}</Td>
      <Td>
        {/* Unattributed orders are the ones worth noticing, so the buyer
            cell never silently drops to a blank — it links only when we
            actually matched a subscriber. */}
        {/* The link goes to who PAID. On a gift the line's subscriber
            is the recipient, and linking the buyer's name to them sent
            the operator to the wrong person. */}
        {o.payer_subscriber_id ? (
          <Link
            to={`/subscribers/${o.payer_subscriber_id}`}
            className="text-paper hover:text-amber-glow hover:underline"
          >
            {name}
          </Link>
        ) : (
          <span className="text-paper">{name}</span>
        )}
        {o.payer_email && o.payer_full_name && (
          <div className="text-paper-trace text-[11px] font-mono">{o.payer_email}</div>
        )}
        {o.gift_recipient_name && (
          <div className="text-[11px] text-paper-trace">
            gift for{' '}
            {o.subscriber_id ? (
              <Link
                to={`/subscribers/${o.subscriber_id}`}
                className="text-paper-dim hover:text-amber-glow hover:underline"
              >
                {o.gift_recipient_name}
              </Link>
            ) : (
              o.gift_recipient_name
            )}
          </div>
        )}
        {!o.subscriber_id && (
          <div className="label-amber text-[10px]">unmatched</div>
        )}
      </Td>
      <Td>
        {o.publication_slug ? (
          <PubBadge slug={o.publication_slug} compact />
        ) : (
          <span className="text-paper-trace">{o.publication_name ?? '—'}</span>
        )}
      </Td>
      <Td subtle>
        {o.kind !== 'payment' ? (
          <span className="label-amber">{o.kind}</span>
        ) : (
          o.new_or_renewal ?? '—'
        )}
      </Td>
      <Td mono subtle>{o.term_months ? `${o.term_months} mo` : '—'}</Td>
      <Td subtle>
        {providerLabel(o.source)}
        {o.last4 && (
          <span className="text-paper-trace font-mono text-[11px]">
            {' '}· {o.card_type ?? 'card'} ••{o.last4}
          </span>
        )}
      </Td>
      <Td mono subtle>{o.promo_code ?? '—'}</Td>
      <Td mono className="text-right">{fmtMoney(o.amount_paid, false, 2)}</Td>
      <Td>
        <Link to={`/orders/${o.id}`} className="label-amber hover:underline">
          Open →
        </Link>
      </Td>
    </tr>
  )
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="label py-2 px-4 font-normal text-left">{children}</th>
}

function Td({
  children, mono, subtle, className = '',
}: {
  children: React.ReactNode
  mono?: boolean
  subtle?: boolean
  className?: string
}) {
  return (
    <td
      className={`py-2 px-4 text-[13px] align-top ${mono ? 'font-mono' : ''} ${
        subtle ? 'text-paper-dim' : 'text-paper'
      } ${className}`}
    >
      {children}
    </td>
  )
}
